/**
 * Vendor Portal API Client — a DEDICATED axios instance for the vendor login
 * portal, fully isolated from the staff client in `src/lib/api.ts`.
 *
 * Why a separate client?
 *   The vendor portal is a public, self-service surface for field workers on
 *   their phones. It must NEVER share a session with the staff app — a logged-in
 *   PM and a vendor could be on the same device, and the vendor session must not
 *   read or clobber the staff `accessToken`/`userType` keys.
 *
 * Token storage:
 *   - `vendorAccessToken`  / `vendorRefreshToken` in localStorage (distinct keys).
 *
 * Auth contract (per the backend, responses are TOP-LEVEL, not wrapped in {data}):
 *   - Request interceptor attaches `Authorization: Bearer <vendorAccessToken>`.
 *   - On 401, a single-flight refresh hits POST /vendor/auth/refresh and reads
 *     top-level {accessToken, refreshToken}. On refresh failure the vendor keys
 *     are cleared and the browser is redirected to /vendor/login.
 */

import axios from 'axios';
import { makeValidationMessageReadable } from './api';

// Mirror the staff client's API base so both hit the same backend mount.
const API_BASE = '/api/v1';

const VENDOR_ACCESS_KEY = 'vendorAccessToken';
const VENDOR_REFRESH_KEY = 'vendorRefreshToken';

const vendorApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// ── Session helpers ─────────────────────────────────────────────────────────

export function setVendorSession(accessToken: string, refreshToken: string): void {
  localStorage.setItem(VENDOR_ACCESS_KEY, accessToken);
  localStorage.setItem(VENDOR_REFRESH_KEY, refreshToken);
}

export function clearVendorSession(): void {
  localStorage.removeItem(VENDOR_ACCESS_KEY);
  localStorage.removeItem(VENDOR_REFRESH_KEY);
}

export function getVendorAccessToken(): string | null {
  return localStorage.getItem(VENDOR_ACCESS_KEY);
}

export function getVendorRefreshToken(): string | null {
  return localStorage.getItem(VENDOR_REFRESH_KEY);
}

export function hasVendorSession(): boolean {
  return !!localStorage.getItem(VENDOR_ACCESS_KEY);
}

// ── Race-safe refresh — one refresh call shared by all concurrent 401s ────────

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

vendorApi.interceptors.request.use((config) => {
  const token = getVendorAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * A 401 from a vendor login-code endpoint (request-code / verify-code) means BAD
 * CREDENTIALS (wrong code), not an expired session — let the login screen show it
 * inline instead of redirecting to /vendor/login, which would reload the page and
 * throw away a still-valid code the vendor merely mistyped (and block the OTP
 * attempt cap). Mirrors `isAuthFlowRequest` in api.ts.
 */
function isVendorAuthFlowRequest(url?: string): boolean {
  if (!url) return false;
  // Anchored to full path segments so no future authenticated route can match.
  return /\/(verify-code|request-code)(\/|$)/.test(url);
}

vendorApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // The vendor portal is a SECOND axios instance. The staff client got the
    // readable-validation-message rewrite and this one did not, so every
    // validation failure on the 12 vendor-portal doors surfaced as the raw
    // first Zod sentence — untranslated, naming no field. Vendors know nothing
    // about this app; they are the last people who should read Zod prose.
    makeValidationMessageReadable(error);
    if (error.response?.status !== 401 || !original || original._retry || isVendorAuthFlowRequest(original.url)) {
      return Promise.reject(error);
    }

    original._retry = true;
    const refreshToken = getVendorRefreshToken();

    if (!refreshToken) {
      clearVendorSession();
      window.location.href = '/vendor/login';
      return Promise.reject(error);
    }

    // If a refresh is already in flight, queue this request to replay after it.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        addRefreshSubscriber((newToken: string) => {
          if (!newToken) {
            reject(error);
            return;
          }
          original.headers.Authorization = `Bearer ${newToken}`;
          resolve(vendorApi(original));
        });
      });
    }

    isRefreshing = true;

    try {
      // Use a bare axios call (no interceptors) so a 401 on refresh can't recurse.
      const { data } = await axios.post(`${API_BASE}/vendor/auth/refresh`, { refreshToken });
      // Vendor endpoints return TOP-LEVEL tokens (not wrapped in {data}).
      const newAccessToken: string = data.accessToken;
      const newRefreshToken: string = data.refreshToken;
      setVendorSession(newAccessToken, newRefreshToken);
      isRefreshing = false;

      onRefreshed(newAccessToken);
      original.headers.Authorization = `Bearer ${newAccessToken}`;
      return vendorApi(original);
    } catch (refreshErr) {
      isRefreshing = false;
      // Let any queued requests reject too.
      onRefreshed('');
      clearVendorSession();
      window.location.href = '/vendor/login';
      return Promise.reject(refreshErr);
    }
  },
);

export default vendorApi;
